import { motion } from "framer-motion";
import { CheckCircle2, X } from "lucide-react";
import BuyButton from "./CtaButton";
import { useCountdown, pad } from "../lib/useCountdown";
import { PRICE } from "../config";

const EXPENSIVE = [
    "Limited Knowledge",
    "High Cost Per Book",
    "No Bonuses",
    "Single Category Only",
];

const SMART = [
    "Unlimited Knowledge Vault",
    "16+ Premium Categories",
    "Lifetime Access Forever",
    "500GB Audiobooks FREE Bonus",
];

export default function OfferCard() {
    const { h, m, s } = useCountdown();
    return (
        <section
            id="offer"
            data-testid="pricing-offer-card"
            className="scroll-mt-24 bg-white py-16 sm:py-24"
        >
            <div className="mx-auto max-w-5xl px-4 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6 }}
                    className="mx-auto max-w-2xl text-center"
                >
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-orange-600">
                        ✦ Festive Offer ✦
                    </p>
                    <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                        Are You Making the{" "}
                        <span className="text-emerald-600">Smart Choice?</span>
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                        Many useful eBooks individually cost ₹299–₹499+. Here,
                        you unlock a giant bundle for just ₹{PRICE.current}.
                    </p>
                </motion.div>

                <div className="mt-12 grid items-stretch gap-5 md:grid-cols-2 md:gap-6 lg:gap-8">
                    {/* The expensive way */}
                    <motion.div
                        initial={{ opacity: 0, x: -24 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                        data-testid="offer-expensive-card"
                        className="rounded-2xl border border-rose-100 bg-white p-6 shadow-soft sm:p-8"
                    >
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-rose-500 sm:text-xs">
                            The Expensive Way
                        </p>
                        <h3 className="mt-2 font-display text-xl font-bold text-slate-900 sm:text-2xl">
                            1 Book on Amazon
                        </h3>
                        <div className="mt-4 flex items-baseline gap-2">
                            <span className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">
                                ₹299
                            </span>
                            <span className="text-sm text-slate-500">
                                / per book
                            </span>
                        </div>
                        <ul className="mt-6 space-y-3.5">
                            {EXPENSIVE.map((item) => (
                                <li
                                    key={item}
                                    className="flex items-center gap-3 text-sm text-slate-600 sm:text-base"
                                >
                                    <X
                                        size={18}
                                        strokeWidth={2.5}
                                        className="shrink-0 text-rose-400"
                                    />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* The smart way */}
                    <motion.div
                        initial={{ opacity: 0, x: 24 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        data-testid="offer-smart-card"
                        className="relative rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-6 shadow-lift sm:p-8"
                    >
                        <span className="absolute -top-3.5 right-6 whitespace-nowrap rounded-full bg-gradient-to-r from-orange-500 via-amber-400 to-orange-500 px-4 py-1.5 font-display text-[11px] font-extrabold uppercase tracking-widest text-slate-950 shadow-lg shadow-orange-500/30 sm:text-xs">
                            Best Value
                        </span>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-600 sm:text-xs">
                            The Smart Way
                        </p>
                        <h3 className="mt-2 font-display text-xl font-bold text-slate-900 sm:text-2xl">
                            Drvn Dream Mega Bundle
                        </h3>
                        <div className="mt-4 flex flex-wrap items-baseline gap-2">
                            <span className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">
                                ₹{PRICE.current}
                            </span>
                            <span className="text-base text-slate-400 line-through sm:text-lg">
                                ₹{PRICE.original.toLocaleString("en-IN")}
                            </span>
                        </div>
                        <p className="mt-2 text-sm font-bold text-emerald-700">
                            12,000+ eBooks • {PRICE.off}% OFF Today!
                        </p>
                        <ul className="mt-6 space-y-3.5">
                            {SMART.map((item) => (
                                <li
                                    key={item}
                                    className="flex items-center gap-3 text-sm font-medium text-slate-700 sm:text-base"
                                >
                                    <CheckCircle2
                                        size={18}
                                        className="shrink-0 text-emerald-600"
                                    />
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <BuyButton
                            testId="offer-buy-now-btn"
                            className="mt-7 w-full"
                        >
                            Get Instant Access — ₹{PRICE.current} 🔓
                        </BuyButton>
                        <p className="mt-3 text-center text-[11px] font-semibold text-slate-500 sm:text-xs">
                            100% Secure Checkout · UPI / Cards / NetBanking
                        </p>
                        <p className="mt-1 text-center text-[11px] font-bold text-emerald-300 sm:text-xs">
                            ✦ 24-Hour Money-Back Guarantee ✦
                        </p>
                        <p
                            data-testid="offer-countdown"
                            className="mt-1 text-center text-xs text-orange-600"
                        >
                            Price goes back to ₹
                            {PRICE.original.toLocaleString("en-IN")} in{" "}
                            {pad(h)}h {pad(m)}m {pad(s)}s
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
